// Write a comment (spec F4). Plain text up to 4,000 characters; the button disables itself while
// the post runs, so a double click makes one comment (constitution §5.5).
import { COMMENT_MAX_LENGTH } from '@cis/api-client';
import { useId, useState, type FormEvent } from 'react';
import { useAddComment } from '../../api/comments.ts';
import { userMessage } from '../../ui/error-message.ts';
import { useToast } from '../../ui/toast.tsx';

export function CommentForm(props: { readonly taskId: string }): React.JSX.Element {
  const fieldId = useId();
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const add = useAddComment(props.taskId);
  const toast = useToast();

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const body = text.trim();
    if (body === '') {
      setProblem('Write something before you post.');
      return;
    }
    if (body.length > COMMENT_MAX_LENGTH) {
      setProblem(`Keep it under ${String(COMMENT_MAX_LENGTH)} characters.`);
      return;
    }
    add.mutate(body, {
      onSuccess: () => {
        setText('');
        toast.show('success', 'Comment posted.');
      },
      onError: (err) => toast.show('error', userMessage(err)),
    });
  };

  return (
    <form className="comment-form" onSubmit={submit} noValidate>
      <label className="comment-form__label" htmlFor={fieldId}>
        Add a comment
      </label>
      <textarea
        id={fieldId}
        className="comment-form__text"
        rows={3}
        maxLength={COMMENT_MAX_LENGTH}
        value={text}
        aria-invalid={problem !== null}
        aria-describedby={`${fieldId}-count${problem === null ? '' : ` ${fieldId}-error`}`}
        onChange={(e) => {
          setText(e.target.value);
          setProblem(null);
        }}
      />
      <p id={`${fieldId}-count`} className="comment-form__count">
        {text.length} of {COMMENT_MAX_LENGTH} characters
      </p>
      {problem === null ? null : (
        <p id={`${fieldId}-error`} className="comment-form__error" role="alert">
          {problem}
        </p>
      )}
      <button type="submit" className="btn btn--primary" disabled={add.isPending}>
        {add.isPending ? 'Posting…' : 'Post comment'}
      </button>
    </form>
  );
}
